'use client'

import { motion } from 'framer-motion'
import { Target, Zap, Award, Users, Shield, Clock, CheckCircle2 } from 'lucide-react'
import Link from 'next/link'
import DynamicCompanyInfo from './DynamicCompanyInfo'
import SmartImage from './ui/SmartImage'
import { siteImages } from '@/data/siteImages'

export default function WhyChooseUs() {
  const reasons = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Market Analysis',
      description: 'A market analysis is the most important part of your business plan. Our team helps to reduce problems and create opportunities for clients & fulfill requirements according to sitemap designed.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Strategic Planning',
      description: "It's an important step because it is the skeleton of your site. Our team creates the data that gives customers an opportunity to judge how the entire site or application will look like.",
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Design Mockups',
      description: 'We make something new every day. We make it easy to bring your ideas to people with plenty of tools creating quick designs mockups of websites, applications, or anything else you can imagine.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Development',
      description: 'In our development process, it is important to set up frameworks and CMS to make sure that everything will fit onto the design and sitemap at the development phase.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Testing & Delivery',
      description: 'Our fundamental job of test execution depends on how successful the deployment was. Before deploying, our QA team identifies and fixes bugs to create success stories for your company.',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Maintenance',
      description: 'We are here! After deployment, our team continues providing support to our customers. To maintain your website, you need to update all useful information on time.',
      color: 'from-teal-500 to-green-500'
    }
  ]

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4"
          >
            WHY CHOOSE US
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="gradient-text">Development Process</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            <DynamicCompanyInfo type="companyName">
              {(companyInfo) => (
                <>
                  {companyInfo?.companyName || 'Our company'} has been a prominent leader in providing an extensive series of IT services 
                  with high importance on establishing long-term associations with our clients.
                </>
              )}
            </DynamicCompanyInfo>
          </p>
        </motion.div>

        {/* Image showcase */}
        <div className="grid lg:grid-cols-2 gap-10 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative h-72 sm:h-96 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5">
              <SmartImage
                src={siteImages.about.culture}
                alt="Our team collaborating on client solutions"
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/40 to-transparent mix-blend-multiply" />
            </div>
            {/* Floating stat card */}
            <motion.div
              animate={{ y: [-6, 6, -6] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -bottom-6 -right-4 bg-white rounded-2xl shadow-xl p-5 border border-gray-100 hidden sm:block"
            >
              <div className="text-3xl font-bold gradient-text">98%</div>
              <div className="text-xs text-gray-500">On-time delivery</div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              A partner invested in your <span className="gradient-text">success</span>
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              From the first conversation to long after launch, we work as an extension of your team — bringing
              senior engineers, clear communication, and a process refined across hundreds of projects.
            </p>
            <ul className="space-y-3">
              {[
                'Dedicated project manager on every engagement',
                'Transparent timelines and weekly demos',
                'Senior-led engineering with code reviews',
                'Post-launch support and maintenance',
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-gray-700">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <div className="relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-gray-200 h-full">
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${reason.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}></div>
                
                {/* Icon */}
                <div className={`relative w-16 h-16 bg-gradient-to-br ${reason.color} rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {reason.icon}
                </div>
                
                {/* Content */}
                <div className="relative">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors duration-300">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {reason.description}
                  </p>
                </div>

                {/* Decorative Element */}
                <div className={`absolute top-6 right-6 w-20 h-20 bg-gradient-to-br ${reason.color} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300`}></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h3>
            <p className="text-xl mb-6 opacity-90">
              Let's discuss how we can help transform your business with our proven development process.
            </p>
            <Link
              href="/contact"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 inline-block"
            >
              Get Free Consultation
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  )
}